import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Callout, Text, Title } from '@tremor/react';
import { currentUser } from '../api/auth';
import { ackSupportTickets, fetchSupportBoard } from '../api/supportTickets';
import type { SupportBoard, SupportBoardTicket } from '../api/supportTickets';
import type { User } from '../api/types';
import { t } from '../lib/i18n';
import Header from '../components/Header';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import EmptyState from '../components/EmptyState';
import TicketBoard from './support/TicketBoard';
import TicketDetailPanel from './support/TicketDetailPanel';

export default function SupportTickets() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser().then((u) => { if (alive) setUser(u); }).catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <SupportTicketsInner user={user} />;
}

function SupportTicketsInner({ user }: { user: User }) {
  const [board, setBoard] = useState<SupportBoard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<SupportBoardTicket | null>(null);

  const load = () => {
    setErr(null);
    setBoard(null);
    fetchSupportBoard()
      .then((b) => {
        setBoard(b);
        // Fire-and-forget: marks everything read for this user. Board is
        // already rendered by the time this settles, so a failure here
        // (same licensing-server hiccup that can also set offline:true)
        // must not surface as a page error.
        ackSupportTickets().catch(() => {});
      })
      .catch((e: Error) => setErr(e.message));
  };
  // Refresh on open only, per spec — no polling. The retry button below
  // re-runs the same loader.
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div className="flex items-baseline gap-3">
          <Title>{t.supportTickets.title}</Title>
          <Text className="text-tremor-content text-sm">{t.supportTickets.subtitle}</Text>
          <div className="ml-auto flex gap-2">
            <Button
              variant="secondary"
              onClick={() => window.dispatchEvent(new CustomEvent('pacebi:support-open', { detail: { tab: 'support' } }))}
            >
              {t.supportTickets.reportButton}
            </Button>
          </div>
        </div>

        {board?.offline && (
          <Callout title={t.supportTickets.offlineTitle} color="amber">
            {t.supportTickets.offlineBody}
          </Callout>
        )}

        {err && <ErrorRetry message={err} onRetry={load} />}
        {!err && !board && (
          <div aria-label={t.supportTickets.loading}>
            <TableSkeleton rows={4} cols={4} />
          </div>
        )}
        {!err && board && board.tickets.length === 0 && <EmptyState message={t.supportTickets.empty} />}
        {!err && board && board.tickets.length > 0 && (
          <TicketBoard tickets={board.tickets} onOpen={setSelected} />
        )}
      </main>

      {selected && (
        <TicketDetailPanel
          ticket={selected}
          authorName={user.username}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
